import makeStyles from '@mui/styles/makeStyles'

const PANEL_WIDTH = 400

export const useStyles = makeStyles(({ spacing, palette, typography, shadows, breakpoints }) => ({
  /* ---------------------------------------------------------------- bell */
  bellButton: {
    color: palette.text.primary,
    '&:hover': {
      backgroundColor: palette.action.hover
    }
  },
  bellBadge: {
    '& .MuiBadge-badge': {
      fontSize: '0.6875rem',
      fontWeight: typography.fontWeightBold,
      minWidth: 18,
      height: 18,
      padding: '0 4px'
    }
  },

  /* --------------------------------------------------------------- panel */
  // Desktop popover surface. Mobile uses the drawer paper below.
  popoverPaper: {
    width: PANEL_WIDTH,
    maxWidth: 'calc(100vw - 32px)',
    borderRadius: spacing(1),
    boxShadow: shadows[8],
    overflow: 'hidden'
  },
  drawerPaper: {
    borderTopLeftRadius: spacing(1.5),
    borderTopRightRadius: spacing(1.5),
    maxHeight: '85vh'
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },

  /* -------------------------------------------------------------- header */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(2),
    padding: spacing(2, 2.5)
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: typography.fontWeightBold,
    color: palette.text.primary
  },
  // Text-only affordance; `&&` beats the MUI Button text-transform/padding defaults.
  markAllReadButton: {
    '&&': {
      padding: 0,
      minWidth: 'auto',
      textTransform: 'none',
      fontSize: '0.875rem',
      fontWeight: typography.fontWeightRegular,
      color: palette.text.secondary
    },
    '&&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline'
    },
    '&&.Mui-disabled': {
      color: palette.text.disabled
    }
  },

  /* ---------------------------------------------------------------- tabs */
  tabs: {
    minHeight: 'auto',
    borderBottom: `1px solid ${palette.divider}`,
    padding: spacing(0, 2.5),
    '& .MuiTabs-indicator': {
      height: 2,
      backgroundColor: palette.text.primary
    }
  },
  tab: {
    '&&': {
      minHeight: 'auto',
      minWidth: 'auto',
      padding: spacing(0, 0, 1.5, 0),
      marginRight: spacing(3),
      textTransform: 'none',
      fontSize: '0.9375rem',
      color: palette.text.secondary
    },
    '&&.Mui-selected': {
      color: palette.text.primary,
      fontWeight: typography.fontWeightBold
    }
  },

  /* ---------------------------------------------------------------- list */
  list: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 0,
    [breakpoints.up('sm')]: {
      maxHeight: 380
    }
  },

  /* ---------------------------------------------------------------- item */
  item: {
    '&&': {
      display: 'flex',
      alignItems: 'flex-start',
      gap: spacing(1.5),
      padding: spacing(1.5, 2.5),
      textAlign: 'left'
    },
    '&&:hover': {
      backgroundColor: palette.action.hover
    }
  },
  itemUnread: {
    backgroundColor: palette.action.selected
  },
  avatar: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.action.selected,
    color: palette.text.secondary
  },
  itemBody: {
    flex: 1,
    minWidth: 0
  },
  itemTitle: {
    fontSize: '0.9375rem',
    fontWeight: typography.fontWeightMedium,
    color: palette.text.primary
  },
  itemSubtitle: {
    fontSize: '0.875rem',
    color: palette.text.secondary
  },
  itemMeta: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: spacing(0.75),
    paddingTop: spacing(0.25)
  },
  timeAgo: {
    fontSize: '0.8125rem',
    color: palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  // Paired with a visually-hidden "Unread" label on the row so the state is
  // never signalled by the dot alone.
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: palette.primary.main
  },
  unreadDotPlaceholder: {
    width: 8,
    height: 8
  },

  /* Visually hidden but announced — matches the pattern in
     pages/accelerate/components/skeleton. */
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0
  },

  /* ------------------------------------------- empty / error state block */
  // Shared by the empty and error states so both sit at the same optical
  // centre as a populated list.
  stateBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing(1),
    padding: spacing(5, 3)
  },
  stateTitle: {
    fontSize: '0.9375rem',
    fontWeight: typography.fontWeightMedium,
    color: palette.text.primary,
    textAlign: 'center'
  },
  stateBody: {
    fontSize: '0.875rem',
    color: palette.text.secondary,
    textAlign: 'center'
  },
  retryButton: {
    '&&': {
      marginTop: spacing(1),
      textTransform: 'none',
      fontSize: '0.875rem'
    }
  },

  /* ------------------------------------------------------ loading state */
  skeletonRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing(1.5),
    padding: spacing(2, 2.5),
    borderBottom: `1px solid ${palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  skeletonLines: {
    flex: 1,
    minWidth: 0
  },

  /* -------------------------------------------------------------- footer */
  footer: {
    borderTop: `1px solid ${palette.divider}`
  },
  footerButton: {
    '&&': {
      width: '100%',
      padding: spacing(1.75),
      textTransform: 'none',
      fontSize: '0.9375rem',
      fontWeight: typography.fontWeightMedium,
      color: palette.text.primary
    },
    '&&:hover': {
      backgroundColor: palette.action.hover,
      textDecoration: 'none'
    }
  },

  /* ---------------------------------------------------- mobile chrome */
  mobileHandle: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: spacing(1)
  },
  mobileHandleBar: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: palette.divider
  }
}))
